import FormService from '../../../core/services/FormService';
import { isRequired } from '../../../core/validations';
import NotificationService from '../../../core/services/NotificationService';
import BillFormService from './BillFormService';
import { get } from 'lodash';
import ConfirmationDialogService from '../../../core/services/ConfirmationDialogService';
import FetchService from '../../../core/services/FetchService';
import { action, extendObservable } from 'mobx';
import moment from 'moment';

class BillProductsFormService {
  fetch = new FetchService();
  form = new FormService();

  constructor() {
    extendObservable(this, {
      products: [],
    });
    this.form.validations = {
      hs_sku: [isRequired],
      unitPrice: [isRequired],
      description: [(value, allValues) => (allValues.hs_sku === 'others' ? isRequired(value) : false)],
    };
  }

  init = () => {
    this.form.reset();
    this.loadProducts();
  };

  loadProducts = action(() => {
    this.fetch
      .fetch({
        method: 'get',
        url: '/hubspot/products',
      })
      .then(() => {
        if (this.fetch.data) {
          this.products = [
            ...this.fetch.data.map((product) => ({
              ...product,
              value: product.hs_sku,
              label: product.name,
            })),
          ].sort((a, b) => a.label.localeCompare(b.label));
        }
      });
  });

  handleChange = (path, value) => {
    if (path === 'hs_sku') {
      this.form.reset();
      const product = BillFormService.contract.products.find((product) => product.hs_sku === value);
      if (product) {
        this.form.setValue('amount', product.installments ? null : product.amount);
        this.form.setValue(
          'unitPrice',
          product.installments
            ? ((product.amount || 1) * product.finalUnitPrice) / product.installments
            : product.finalUnitPrice
        );
        this.fetch
          .fetch({
            url: `/contracts/${BillFormService.form.getValue('contract')}/products/${value}/next-installment-number`,
            apiVersion: 'v2',
          })
          .then(() => {
            if (this.fetch.data) {
              this.form.setValue('installmentNumber', this.fetch.data.nextInstallmentNumber);
            }
          });
      }
    }
    this.form.setValue(path, value);
  };

  handleSubmit = () => {
    this.form.submitted = true;
    if (this.form.errors) {
      NotificationService.addNotification('Fill the required fields', 'error');
      return;
    }
    const products = [...BillFormService.form.getValue('products')];
    const contractProduct = BillFormService.contract.products.find(
      (product) => product.value === get(this.form.getValues(), 'hs_sku', null)
    );
    const selectedProduct = this.products.find(
      (product) => product.value === get(this.form.getValues(), 'hs_sku', null)
    );
    const product = {
      ...this.form.getValues(),
      ...(contractProduct && { installments: contractProduct.installments }),
      name: selectedProduct.label,
    };

    if (this.form.getValue('id')) {
      const index = products.findIndex((product) => product.id === this.form.getValue('id'));
      products.splice(index, 1, product);
      BillFormService.form.setValue('products', products);
    } else if (
      product.hs_sku === 'others' ||
      !products.some((product) => product.hs_sku === get(this.form.getValues(), 'hs_sku', null))
    ) {
      products.push({
        ...product,
        id: moment().format(),
      });
      BillFormService.form.setValue('products', products);
    }
    this.form.reset();
  };

  handleEdit = ({ id }) => {
    this.form.reset();
    const product = {
      ...BillFormService.form.getValue('products').find((product) => product.id === id),
      installments: null,
    };
    for (const key of Object.keys(product)) {
      this.form.setValue(key, product[key]);
    }
  };

  handleDelete = (product) => {
    ConfirmationDialogService.show(
      'Delete Product',
      `Do you really want to delete product ${product.name}${product.description ? `- ${product.description}` : ''}?`,
      () => {
        const products = [...BillFormService.form.getValue('products')];
        BillFormService.form.setValue(
          'products',
          products.filter((p) => p.id !== product.id)
        );
      }
    );
  };
}

const billProductsFormService = new BillProductsFormService();

export default billProductsFormService;
